import { useAutoAnimate } from '@formkit/auto-animate/react'
import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { Location } from 'history'
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { CreateVirtualCardScreen } from 'src/app/CreateVirtualCardForm/CreateVirtualCardScreen'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import SvgAttachmentFile from 'src/components/Svgs/SvgAttachmentFile'
import SvgBudgetCreateLabel from 'src/components/Svgs/SvgBudgetCreateLabel'
import SvgBudgetRequestLabel from 'src/components/Svgs/SvgBudgetRequestLabel'
import SvgCardCreateLabel from 'src/components/Svgs/SvgCardCreateLabel'
import SvgCardRequestLabel from 'src/components/Svgs/SvgCardRequestLabel'
import SvgExpense from 'src/components/Svgs/SvgExpense'
import SvgPayInvoice from 'src/components/Svgs/SvgPayInvoice'
import { OrganizationRoles } from 'src/enums'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useBackgroundDispatch } from 'src/hooks'
import Trns from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import {
  actionUnavailableUrl,
  createBillPayUrl,
  createBudgetUrl,
  createVirtualCardUrl,
  requestBudgetUrl,
  requestVirtualCardUrl,
} from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import {
  createSetIsCreateExpenseReportPopupOpen,
  createSetIsSetupOrganizationPopupOpen,
} from 'src/redux/actions/app.actions'
import { LocationState } from 'src/redux/createRootReducer'
import {
  selectAllStatisticsLoadedFromState,
  selectStatisticsDispatcherFromState,
} from 'src/redux/selectors/app.selectors'
import {
  selectIsUserOrglessRCNUser,
  selectUserListModelFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectOutOfPocketExpensesVisible } from 'src/redux/selectors/data/organizations.selectors'
import {
  selectCreditCardsActiveCanIssueVirtualCardsCountStatisticsFromState,
  selectCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFromState,
  selectCreditCardsCanIssueVirtualCardsCountStatisticsFromState,
  selectCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFromState,
} from 'src/redux/selectors/data/statistics.selectors'
import {
  selectCalculatedBackgroundLocation,
  selectLocationFromState,
} from 'src/redux/selectors/router.selectors'
import { selectBrandFeaturesFromState } from 'src/redux/selectors/session.selectors'
import variables from 'src/style/variables.json'
import { css } from 'styled-components'

export type CreateNewDropdownOption =
  | CreateNewDropdownOptionAriaSource
  | CreateNewDropdownOptionAriaLabel

type CreateNewDropdownSection = {
  id: string
  label: string
  options: CreateNewDropdownOption[]
}

interface CreateNewDropdownOptionShared {
  id?: string
  label: React.ReactElement
  image?: React.ReactElement
  to?: string | Location<LocationState>
  onClick?: () => void
  grayed?: boolean
  key?: string | number
  subtitle?: React.ReactElement
}

interface CreateNewDropdownOptionAriaSource
  extends CreateNewDropdownOptionShared {
  ariaLabelSource: string
  ariaLabel?: undefined
}

interface CreateNewDropdownOptionAriaLabel
  extends CreateNewDropdownOptionShared {
  ariaLabel: string
  ariaLabelSource?: undefined
}

interface CreateNewDropdownProps {
  paddingTop?: boolean
  rightOffset?: number
  isScrolled?: boolean
  isSecondaryCta?: boolean
}

const CreateNewDropdown: React.FC<CreateNewDropdownProps> = (props) => {
  const buttonRef: React.MutableRefObject<HTMLDivElement | null> = useRef(null)
  const { analytics } = useAnalyticsContext()

  const outOfPocketExpensesEnabled = useSelector(
    selectOutOfPocketExpensesVisible,
  )

  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)
  const creditCardsActiveCanIssueVirtualCardsCount = useSelector(
    selectCreditCardsActiveCanIssueVirtualCardsCountStatisticsFromState,
  )
  const creditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount = useSelector(
    selectCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFromState,
  )
  const creditCardsCanIssueVirtualCardsCount = useSelector(
    selectCreditCardsCanIssueVirtualCardsCountStatisticsFromState,
  )
  const creditCardsSourceBudgetsEnabledCanIssueBudgetsCount = useSelector(
    selectCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFromState,
  )
  const isUserOrglessRCNUser = useSelector(selectIsUserOrglessRCNUser)
  const currentLocation = useSelector(selectLocationFromState)
  const user = useSelector(selectUserListModelFromState)
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: user?.organizationId,
  })
  const showGuestRole = orgFeatureFlags?.guestRoleEnabled
  const isGuestUser =
    user?.organizationRole === OrganizationRoles.guest && showGuestRole
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const { CREATE_BILL_PAY_CARD: enableCreateBillPayFeature } =
    useQueryUserFeatures([UserFeature.CREATE_BILL_PAY_CARD]).data || {}
  const { billPayEnabled } = useSelector(selectBrandFeaturesFromState)

  const enableBillPay = enableCreateBillPayFeature && billPayEnabled

  const [dropdownRef] = useAutoAnimate({ duration: 100, easing: 'ease-in' })

  const dispatch = useBackgroundDispatch()

  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)

  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  useEffect((): void => {
    dispatchStatistics([
      appStatisticsKeys.CreditCardsActiveCanIssueVirtualCardsCountStatistics,
      appStatisticsKeys.CreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatistics,
      appStatisticsKeys.CreditCardsCanIssueVirtualCardsCountStatistics,
      appStatisticsKeys.CreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatistics,
    ])
  }, [dispatchStatistics])

  const handleHoverButtonAnalytics = (): void => {
    analytics?.track('create new button', {
      screen: calculatedBackgroundLocation.pathname,
    })
  }

  const listItem = (
    option: CreateNewDropdownOption,
    i: number,
  ): React.ReactElement => (
    <HoverableLink
      id={option.id}
      testId={option.id}
      css={css`
        display: flex;
        white-space: wrap;
        ${getFontCss(
          14,
          themeConstants.colors.cta,
          styleConstants.fontFamilies.medium,
        )};
        align-items: start;
      `}
      key={i}
      to={option.to}
      {...(option.ariaLabelSource !== undefined
        ? { ariaLabelSource: option.ariaLabelSource }
        : { ariaLabel: option.ariaLabel })}
      onClick={(): void => {
        option.onClick && option.onClick()
      }}
    >
      {option.image && (
        <div
          css={css`
            display: flex;
            margin-right: 5px;
          `}
        >
          {option.image}
        </div>
      )}
      <div>
        <div
          css={css`
            ${!option.subtitle &&
            `
            margin-top: 5px;
          `}
            &:hover {
              text-decoration: underline;
            }
          `}
        >
          {option.label}
        </div>
        {option.subtitle && (
          <div
            css={css`
              ${getFontCss(
                10,
                styleConstants.colors.gray_dark,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            {option.subtitle}
          </div>
        )}
      </div>
    </HoverableLink>
  )

  const createVcAvailableOption: CreateNewDropdownOption[] | undefined =
    creditCardsCanIssueVirtualCardsCount > 0 &&
    creditCardsActiveCanIssueVirtualCardsCount > 0
      ? [
          {
            id: 'createVcnAvailNavTop',
            label: <Trns source={'Create virtual cards'} />,
            to: createVirtualCardUrl(),
            onClick: (): void => {
              analytics?.track('create virtual card flyout - click-in')
            },
            ariaLabelSource: 'Create virtual cards',
            image: <SvgCardCreateLabel />,
          },
        ]
      : undefined
  const createVcUnavailableOption: CreateNewDropdownOption[] | undefined =
    creditCardsCanIssueVirtualCardsCount > 0 &&
    creditCardsActiveCanIssueVirtualCardsCount === 0
      ? [
          {
            id: 'createVcnUnavailNavTop',
            label: <Trns source={'Create virtual cards'} />,
            to: actionUnavailableUrl(),
            grayed: true,
            ariaLabelSource: 'Create virtual cards',
            image: <SvgCardCreateLabel />,
          },
        ]
      : undefined
  const requestBudgetAvaiableOption: CreateNewDropdownOption[] | undefined = [
    {
      id: 'requestBudgetAvailNavTop',
      label: <Trns source={'Request a budget'} />,
      to: requestBudgetUrl(),
      onClick: (): void => {
        analytics?.track('request budget')
      },
      ariaLabelSource: 'Request a budget',
      subtitle: <Trns source={'Delegate creation permissions'} />,
      image: <SvgBudgetRequestLabel />,
    },
  ]
  const createBudgetAvailableOption: CreateNewDropdownOption[] | undefined =
    creditCardsSourceBudgetsEnabledCanIssueBudgetsCount > 0 &&
    creditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount > 0
      ? isUserOrglessRCNUser
        ? [
            {
              id: 'createBudgetAvailNavTop',
              label: <Trns source={'Create a budget'} />,
              to: currentLocation.pathname,
              onClick: (): void => {
                dispatch(
                  createSetIsSetupOrganizationPopupOpen(
                    true,
                    createBudgetUrl(),
                  ),
                )
              },
              ariaLabelSource: 'Create a budget',
              subtitle: <Trns source={'Delegate creation permissions'} />,
              image: <SvgBudgetCreateLabel />,
            },
          ]
        : [
            {
              id: 'createBudgetAvailNavTop',
              label: <Trns source={'Create a budget'} />,
              to: createBudgetUrl(),
              onClick: (): void => {
                // New Universal Mixpanel Event
                analytics?.track('create budget flyout - click-in')
              },
              ariaLabelSource: 'Create a budget',
              subtitle: <Trns source={'Get access to create virtual cards'} />,
              image: <SvgBudgetCreateLabel />,
            },
            {
              id: 'requestBudgetAvailNavTop',
              label: <Trns source={'Request a budget'} />,
              to: requestBudgetUrl(),
              onClick: (): void => {
                // New Universal Mixpanel Event
                analytics?.track('request budget flyout - click-in')
              },
              ariaLabelSource: 'Request a budget',
              subtitle: <Trns source={'Delegate creation permissions'} />,
              image: <SvgBudgetRequestLabel />,
            },
          ]
      : undefined
  const createBudgetUnavailableOption: CreateNewDropdownOption[] | undefined =
    creditCardsSourceBudgetsEnabledCanIssueBudgetsCount > 0 &&
    creditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount === 0
      ? [
          {
            id: 'createBudgetUnavailNavTop',
            label: <Trns source={'Create a budget'} />,
            to: actionUnavailableUrl(),
            grayed: true,
            ariaLabelSource: 'Create a budget',
            subtitle: <Trns source={'Delegate creation permissions'} />,
            image: <SvgBudgetCreateLabel />,
          },
        ]
      : undefined
  const createInBulkAvailableOption: CreateNewDropdownOption[] | undefined =
    creditCardsCanIssueVirtualCardsCount > 0 &&
    creditCardsActiveCanIssueVirtualCardsCount > 0
      ? [
          {
            id: 'createInBulkAvailNavTop',
            label: <Trns source={'Create in bulk (CSV)'} />,
            to: {
              pathname: createVirtualCardUrl(),
              search: '',
              state: { screen: CreateVirtualCardScreen.Multiple },
              hash: '',
            },
            onClick: (): void => {
              analytics?.track('create in bulk (csv) flyout - click-in')
            },
            ariaLabelSource: 'Create in bulk (CSV)',
            image: <SvgAttachmentFile />,
          },
        ]
      : undefined
  const createInBulkUnavailableOption: CreateNewDropdownOption[] | undefined =
    creditCardsCanIssueVirtualCardsCount > 0 &&
    creditCardsActiveCanIssueVirtualCardsCount === 0
      ? [
          {
            id: 'createInBulkUnavailNavTop',
            label: <Trns source={'Create in bulk (CSV)'} />,
            to: actionUnavailableUrl(),
            grayed: true,
            ariaLabelSource: 'Create in bulk (CSV)',
            image: <SvgAttachmentFile />,
          },
        ]
      : undefined

  const createInvoiceAvailableOption: CreateNewDropdownOption[] | undefined =
    creditCardsCanIssueVirtualCardsCount > 0 &&
    creditCardsActiveCanIssueVirtualCardsCount > 0
      ? [
          {
            id: 'createInvoiceAvailNavTop',
            label: <Trns source={'Pay an invoice'} />,
            to: createBillPayUrl(),
            ariaLabelSource: 'Pay an invoice',
            image: <SvgPayInvoice />,
            subtitle: (
              <Trns source={'Create a virtual card to make a payment'} />
            ),
          },
        ]
      : undefined
  const createInvoiceUnavailableOption: CreateNewDropdownOption[] | undefined =
    creditCardsCanIssueVirtualCardsCount > 0 &&
    creditCardsActiveCanIssueVirtualCardsCount === 0
      ? [
          {
            id: 'createInvoiceUnavailNavTop',
            label: <Trns source={'Pay an invoice'} />,
            to: actionUnavailableUrl(),
            grayed: true,
            ariaLabelSource: 'Pay an invoice',
            image: <SvgPayInvoice />,
            subtitle: (
              <Trns source={'Create a virtual card to make a payment'} />
            ),
          },
        ]
      : undefined
  const createBulkInvoiceAvailableOption:
    | CreateNewDropdownOption[]
    | undefined =
    creditCardsCanIssueVirtualCardsCount > 0 &&
    creditCardsActiveCanIssueVirtualCardsCount > 0
      ? [
          {
            id: 'createBulkInvoiceAvailNavTop',
            label: <Trns source={'Pay multiple invoices (CSV)'} />,
            to: {
              pathname: createBillPayUrl(),
              search: '',
              state: { screen: CreateVirtualCardScreen.MultipleBillPay },
              hash: '',
            },
            ariaLabelSource: 'Pay multiple invoices (CSV)',
            image: <SvgPayInvoice />,
          },
        ]
      : undefined
  const createBulkInvoiceUnavailableOption:
    | CreateNewDropdownOption[]
    | undefined =
    creditCardsCanIssueVirtualCardsCount > 0 &&
    creditCardsActiveCanIssueVirtualCardsCount === 0
      ? [
          {
            id: 'createBulkInvoiceUnavailNavTop',
            label: <Trns source={'Pay multiple invoices (CSV)'} />,
            to: actionUnavailableUrl(),
            grayed: true,
            ariaLabelSource: 'Pay multiple invoices (CSV)',
            image: <SvgPayInvoice />,
          },
        ]
      : undefined
  const createExpenseReportCreateOption: CreateNewDropdownOption[] | undefined =
    [
      {
        id: 'createExpenseReportCreateNavTop',
        label: <Trns source={'Create expense report'} />,
        subtitle: <Trns source={'Submit out-of-pocket expenses'} />,
        onClick: (): void => {
          dispatch(createSetIsCreateExpenseReportPopupOpen(true))
          analytics?.track('create OOPE flyout - click-in')
        },
        ariaLabelSource: 'Create expense report',
        image: <SvgExpense />,
      },
    ]

  const requestVcnOption: CreateNewDropdownOption[] | undefined = !isGuestUser
    ? [
        {
          id: 'requestVcnNavTop',
          label: <Trns source={'Request a virtual card'} />,
          to: requestVirtualCardUrl(),
          onClick: (): void => {
            // New Mixpanel Event
            analytics?.track('request virtual card flyout - click-in')
          },
          ariaLabelSource: 'Request a virtual card',
          image: <SvgCardRequestLabel />,
        },
      ]
    : undefined

  const vcnOptions: CreateNewDropdownSection = {
    id: 'virtualCards',
    label: 'Virtual Cards',
    options: [
      ...(createVcAvailableOption || createVcUnavailableOption || []),
      ...(createInBulkAvailableOption || createInBulkUnavailableOption || []),
      ...(requestVcnOption || []),
    ],
  }

  const budgetOptions: CreateNewDropdownSection = {
    id: 'budgets',
    label: 'Budgets',
    options: [
      ...(createBudgetAvailableOption ||
        createBudgetUnavailableOption ||
        requestBudgetAvaiableOption ||
        []),
    ],
  }

  const billPayOptions: CreateNewDropdownSection = {
    id: 'billPay',
    label: 'Bill Pay',
    options: [
      ...(createInvoiceAvailableOption || createInvoiceUnavailableOption || []),
      ...(createBulkInvoiceAvailableOption ||
        createBulkInvoiceUnavailableOption ||
        []),
    ],
  }

  const expenseOptions: CreateNewDropdownSection = {
    id: 'expenseReports',
    label: 'Expense Reports',
    options: [...createExpenseReportCreateOption],
  }

  const standardOptions = [
    !isGuestUser ? vcnOptions : undefined,
    !isGuestUser ? budgetOptions : undefined,
    enableBillPay && !isGuestUser ? billPayOptions : undefined,
    outOfPocketExpensesEnabled ? expenseOptions : undefined,
  ].filter(
    (section): section is CreateNewDropdownSection =>
      typeof section !== 'undefined',
  )

  return (
    <>
      {(!isGuestUser || outOfPocketExpensesEnabled) && (
        <div
          ref={buttonRef}
          css={css`
            position: relative;
            display: flex;
            align-items: flex-end;
            flex-direction: column;

            ${props.paddingTop &&
            getBreakpointDown(breakpointSizes.XSM)(css`
              margin-top: 12px;
            `)}
          `}
          onMouseEnter={(): void => {
            setIsHovered(true)
            setDropdownOpen(true)
            handleHoverButtonAnalytics()
          }}
          onMouseLeave={(): void => {
            setIsHovered(false)
            setDropdownOpen(false)
          }}
          onBlur={(): undefined => {
            return
          }}
          onFocus={(): undefined => {
            return
          }}
        >
          {
            <div
              id={'topNavCreateNew'}
              data-testid={'topNavCreateNew'}
              data-isopen={dropdownOpen}
            >
              {props.isSecondaryCta ? (
                <HoverableLink
                  css={`
                    padding: 0px ${variables.units['base-unit-8']}px;
                  `}
                  ariaLabel="Create New"
                  onClick={(): void => {
                    if (!isHovered) {
                      setDropdownOpen(!dropdownOpen)
                    }
                  }}
                >
                  <Trns source="Create New +" />
                </HoverableLink>
              ) : (
                <Button
                  css={`
                    padding: 0px ${variables.units['base-unit-8']}px;
                  `}
                  design={ButtonDesigns.designCta}
                  type={ButtonTypes.button}
                  text={<Trns source="Create New +" />}
                  onClick={(): void => {
                    if (!isHovered) {
                      setDropdownOpen(!dropdownOpen)
                    }
                  }}
                />
              )}
            </div>
          }
          <div ref={dropdownRef}>
            {dropdownOpen && (
              <div
                css={css`
                  z-index: 1;
                  position: absolute;
                  right: 0px;
                  border-radius: 10px;
                  display: flex;
                  flex-direction: column;
                  border: 1px solid ${styleConstants.colors.gray_medium};
                  box-shadow: 0 3px 10px 0 rgba(0, 0, 0, 0.15);
                  width: 250px;
                  background-color: ${styleConstants.colors.white};
                `}
              >
                {
                  // all necessary stats are loaded
                  statisticsLoaded.creditCardsActiveCanIssueVirtualCardsCountStatisticsLoaded &&
                    statisticsLoaded.creditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsLoaded &&
                    statisticsLoaded.creditCardsCanIssueVirtualCardsCountStatisticsLoaded &&
                    statisticsLoaded.creditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsLoaded &&
                    statisticsLoaded.creditCardsUserOrViewerMeCountStatisticsLoaded &&
                    standardOptions !== undefined &&
                    standardOptions.map(
                      (section, i) =>
                        section !== undefined &&
                        section.options?.length > 0 && (
                          <div
                            key={`CreateNewDropdownSection${i}`}
                            css={css`
                              border-top: ${i === 0
                                ? `none`
                                : `1px solid ${styleConstants.colors.gray_medium}`};
                              padding-bottom: 10px;
                              padding-top: 10px;
                            `}
                          >
                            <div
                              css={css`
                                ${getFontCss(
                                  14,
                                  styleConstants.colors.gray_darkest,
                                  styleConstants.fontFamilies.medium,
                                )};
                                padding: 4px 15px;
                              `}
                            >
                              <Trns source={section.label} />
                            </div>
                            {section.options.map((item, j) => (
                              <div
                                key={`CreateNewDropdownSection${i}-Option${j}`}
                                css={css`
                                  padding: 4px 15px;
                                  ${item.grayed &&
                                  `
                            opacity: 0.5;
                            cursor: default;
                          `}
                                `}
                              >
                                {listItem(item, i)}
                              </div>
                            ))}
                          </div>
                        ),
                    )
                }
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}

export default CreateNewDropdown
